// CallService.js
import mitt from 'mitt';
import { ensurePeer, makeOffer, cleanupPeer } from './service/peer';
import { wireSignaling } from './service/signaling';
import {
    getMedia,
    reactivateTracks,
    listCameras,
    switchCameraTrack,
    reviveAudioIfNeeded,
    attachAudioGuards,
} from './service/media';

import { applyLeaderFlags } from './service/roles';
import { beginReconnection, tryIceRestartIfLeader, maybeIceRestart } from './service/reconnect';

export default class CallService {
    constructor({ socket, media, iceServers }) {
        this.socket = socket;
        this.media = media;
        this.iceServers = iceServers || [];
        this.emitter = mitt();

        this.pc = null;
        this.localStream = null;
        this.remoteStream = new this.media.MediaStream();

        this.state = 'idle';
        this.partner = null;
        this.callId = null;
        this.chatId = null;
        this.mode = 'audio';
        this.video = false;
        this.myId = null;

        this._facing = 'user';
        this.role = null;
        this.isLeader = false;
        this.isPolite = false;
        this.makingOffer = false;
        this.ignoreOffer = false;

        this._pendingCandidates = [];
        this._ringTimer = null;
        this._statsTimer = null;
        this._reconnectTimer = null;
        this._reconnecting = false;
        this._reconnectStartedAt = 0;
        this._connectedAt = null;
        this._lastIncomingCallId = null;
        this._lastAcceptedCallId = null;

        this._kaChannel = null;
        this._kaTimer = null;
        this._wakeLock = null;

        wireSignaling(this);

        this.socket.on('webrtc:ice-config', (cfg) => {
            if (cfg && Array.isArray(cfg.iceServers)) {
                this.iceServers = cfg.iceServers;
            }
        });

        if (typeof window !== 'undefined') {
            window.addEventListener('beforeunload', () => {
                try {
                    if (this.callId && this.partner) {
                        this.socket.emit('call:end', {
                            callId: this.callId,
                            from: this.myId || window.__ME_ID__ || null,
                            to: this.partner,
                            reason: 'ended',
                        });
                    }
                } catch (_error) { }
            });
        }
    }

    on(evt, cb) {
        this.emitter.on(evt, cb);
        return () => this.emitter.off(evt, cb);
    }

    getLocalStream() {
        return this.localStream;
    }

    getRemoteStream() {
        return this.remoteStream;
    }

    async start({ callId, from, to, video, chatId = null }) {
        if (this.state !== 'idle') throw new Error('busy');

        this.myId = from || window.__ME_ID__ || null;

        if (this.myId && typeof window !== 'undefined') {
            window.__ME_ID__ = String(this.myId);
        }

        this.callId = callId;
        this.partner = to;
        this.chatId = chatId;
        this.video = !!video;
        this.mode = video ? 'video' : 'audio';

        this.role = 'caller';
        this._deriveLeaderFlags();

        await this._getMedia(!!video);

        this.state = 'ringing';

        this.socket.emit('call:invite', {
            callId,
            from: this.myId,
            to,
            video: !!video,
            mode: this.mode,
            chatId,
        });

        this.emitter.emit('ring:start', { mode: 'outgoing' });

        clearTimeout(this._ringTimer);
        this._ringTimer = setTimeout(() => {
            if (this.state === 'ringing') {
                try {
                    this.socket.emit('call:cancel', {
                        callId: this.callId,
                        from: this.myId,
                        to: this.partner,
                        reason: 'missed',
                    });
                } catch (_error) { }

                this._cleanup('missed');
            }
        }, 45_000);
    }

    async accept({ callId, from, to, video }) {
        if (this.state !== 'idle' && this.state !== 'ringing') return;

        this.myId = to || window.__ME_ID__ || null;

        if (this.myId && typeof window !== 'undefined') {
            window.__ME_ID__ = String(this.myId);
        }

        this.callId = callId;
        this.partner = from;
        this.video = !!video;
        this.mode = video ? 'video' : 'audio';

        this.role = 'callee';
        this._deriveLeaderFlags();

        await this._getMedia(!!video);
        await ensurePeer(this);

        clearTimeout(this._ringTimer);

        this.state = 'connecting';
        this.emitter.emit('ring:stop');
        this.emitter.emit('connecting');

        this.socket.emit('call:accept', {
            callId,
            from: this.myId,
            to: this.partner,
        });
    }

    reject({ callId, from, to }) {
        clearTimeout(this._ringTimer);
        this.emitter.emit('ring:stop');

        this.socket.emit('call:reject', {
            callId,
            from: to || this.myId || window.__ME_ID__ || null,
            to: from || this.partner || null,
            reason: 'rejected',
        });

        this._cleanup('rejected');
    }

    hangup(reason = 'ended', from = null, quality = 0) {
        clearTimeout(this._ringTimer);
        this.emitter.emit('ring:stop');

        if (this.callId && this.partner) {
            const eventName = this.state === 'ringing' && this.role === 'caller' ? 'call:cancel' : 'call:end';

            this.socket.emit(eventName, {
                callId: this.callId,
                from: from || this.myId || window.__ME_ID__ || null,
                to: this.partner,
                reason,
                quality,
            });
        }

        this._cleanup(reason);
    }

    toggleMute() {
        const tracks = this.localStream?.getAudioTracks?.() || [];

        tracks.forEach((track) => {
            track.enabled = !track.enabled;
        });

        const enabled = tracks[0]?.enabled ?? false;

        this.emitter.emit('local-mic', { enabled });

        if (this.callId && this.partner) {
            this.socket.emit('call:toggle', {
                callId: this.callId,
                to: this.partner,
                type: 'mute',
                enabled,
            });
        }

        return enabled;
    }

    toggleVideo() {
        const tracks = this.localStream?.getVideoTracks?.() || [];

        tracks.forEach((track) => {
            track.enabled = !track.enabled;
        });

        const enabled = tracks[0]?.enabled ?? false;

        this.emitter.emit('local-cam', { enabled });

        if (this.callId && this.partner) {
            this.socket.emit('call:toggle', {
                callId: this.callId,
                to: this.partner,
                type: 'video',
                enabled,
            });
        }

        return enabled;
    }

    async reportQuality(level) {
        if (!this.callId) return;

        this.socket.emit('call:quality', {
            callId: this.callId,
            level,
        });
    }

    async listCameras() {
        return listCameras(this);
    }

    async switchCamera(next = 'auto') {
        return switchCameraTrack(this, next);
    }

    async _getMedia(video) {
        await getMedia(this, video);
        attachAudioGuards(this);
    }

    _cleanup(reason) {
        cleanupPeer(this, reason);
        this._connectedAt = null;
    }

    _deriveLeaderFlags() {
        applyLeaderFlags(this, this.role);
    }

    _reactivateTracks() {
        reactivateTracks(this);
    }

    _beginReconnection(force) {
        beginReconnection(this, force);
    }

    _tryIceRestart() {
        return tryIceRestartIfLeader(this, 'manual');
    }

    _maybeIceRestart(tag = '') {
        return maybeIceRestart(this, tag);
    }

    async _acquireWakeLock() {
        try {
            if ('wakeLock' in navigator && !this._wakeLock) {
                this._wakeLock = await navigator.wakeLock.request('screen');
            }
        } catch (_error) { }
    }

    _releaseWakeLock() {
        try {
            this._wakeLock?.release?.();
        } catch (_error) { }

        this._wakeLock = null;
    }
}

export { ensurePeer, makeOffer };